
import api from './api';
import { AxiosInstance } from 'axios';
import {
  ApiResponse,
  BasicResponse,
  CreateJobPostRequest,
  EditJobPostRequest,
  JobPostResponse,
  CompanyJobPostsResponse,
  Operation,
  JobApplicationCreateRequest,
  JobApplicationUpdateRequest,
  JobApplicationHistoryResponse,
  UserJobApplicationResponsePaginatedResponse,
  JobApplicantsResponsePaginatedResponse,
  JobPostResponsePaginatedResponse,
} from "../types/api";
import { Job, ApplyResult, ApplyFormData } from '../types/components';

// Mock job data - in a real app, this would come from an API
const mockJobs: { [key: string]: Job } = {
  '1': {
    id: 1,
    title: 'Senior Frontend Engineer',
    company: 'GreenStack Labs',
    location: 'Remote • India',
    type: 'Full-time',
    salary: '₹22L – ₹35L CTC',
    experience: '5+ years',
    description: 'We are looking for a Senior Frontend Engineer to build delightful web experiences. You will lead projects, mentor developers and collaborate closely with design and product.',
    skills: ['React', 'TypeScript', 'Node.js', 'CSS', 'JavaScript'],
    requirements: [
      '5+ years of frontend experience (React preferred)',
      'Strong HTML/CSS/JS fundamentals',
      'Experience with state management and REST/GraphQL',
      'Understanding of performance and accessibility',
    ],
    process: [
      'Intro chat (20 min)',
      'Technical interview + pair programming',
      'System design + product walkthrough',
      'Final culture fit chat',
      'Offer',
    ],
    contact: {
      email: 'careers@greenstack.example',
      recruiter: 'Ayesha Khan',
    },
    postedDate: '2024-01-15',
  },
  '2': {
    id: 2,
    title: 'Frontend Engineer',
    company: 'Dhoondh',
    location: 'Remote',
    type: 'Full-time',
    salary: '₹15L – ₹25L CTC',
    experience: '3+ years',
    description: 'Join our growing team as a Frontend Engineer...',
    skills: ['Vue.js', 'JavaScript', 'CSS', 'HTML'],
    requirements: [
      '3+ years of frontend experience',
      'Strong JavaScript fundamentals',
      'Experience with modern frameworks',
    ],
    process: [
      'Initial screening',
      'Technical interview',
      'Final interview',
      'Offer',
    ],
    contact: {
      email: 'operations@dhoondh.in',
      recruiter: 'HR Team',
    },
    postedDate: '2024-01-14',
  }
};

const jobService = {
  createJobPost: async (jobPostData: CreateJobPostRequest): Promise<BasicResponse<number>> => {
    try {
      const response = await (api as AxiosInstance).post('/api/JobPost/create', jobPostData);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  editJobPost: async (jobPostData: EditJobPostRequest): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).put('/api/JobPost/edit', jobPostData);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
  patchJobPost: async (postId: number, operations: Operation[]): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).patch(`/api/JobPost/${postId}`, operations);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  getJobPost: async (postId: number): Promise<BasicResponse<JobPostResponse>> => {
    try {
      const response = await (api as AxiosInstance).get('/api/JobPost/get', { params: { postId } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  getAllCompanyJobPosts: async (companyId: string): Promise<BasicResponse<CompanyJobPostsResponse>> => {
    try {
      const response = await (api as AxiosInstance).get('/api/JobPost/companyPosts', { params: { companyId } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  deleteJobPost: async (postId: number): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).delete('/api/JobPost/delete', { params: { postId } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get applications for a company (backend should support this; params may vary)
  // getCompanyApplications: async (companyId: string): Promise<ApiResponse<any[]>> => {
  //   const tryEndpoints = [
  //     '/api/Application/getAll',
  //     '/api/Application/list',
  //     '/api/Application/getByCompany',
  //     '/api/JobApplication/getAll',
  //   ];

  //   for (const endpoint of tryEndpoints) {
  //     try {
  //       const response = await (api as AxiosInstance).get(endpoint, { params: { companyId } });
  //       return response.data;
  //     } catch (err: any) {
  //       // Continue trying next endpoint on 404/NotFound
  //       const status = err?.response?.status;
  //       const msg = String(err?.message || '').toLowerCase();
  //       if (status && status !== 404) {
  //         // Non-404 errors: rethrow immediately
  //         throw err.response?.data || err.message;
  //       }
  //       if (!status && msg.includes('404')) {
  //         // Treat string 404 the same as not found
  //         continue;
  //       }
  //       // For other network errors, try next endpoint
  //       continue;
  //     }
  //   }

  //   // If nothing worked, gracefully return empty results so UI shows empty state without error
  //   return { data: [], status: 200, statusText: 'OK', headers: {}, config: {} };
  // },
  createJobApplication: async (
    applicationData: JobApplicationCreateRequest
  ): Promise<number> => {
    try {
      const response = await (api as AxiosInstance).post(
        "/api/JobApplication/create",
        applicationData
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  updateJobApplication: async (
    applicationData: JobApplicationUpdateRequest
  ): Promise<boolean> => {
    try {
      const response = await (api as AxiosInstance).put(
        "/api/JobApplication/update",
        applicationData
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
  getJobApplicationHistory: async (
    applicationId: number
  ): Promise<JobApplicationHistoryResponse[]> => {
    try {
      const response = await (api as AxiosInstance).get(
        `/api/JobApplication/${applicationId}/history`
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
  getUserApplications: async (
    userId: string,
    pageNumber: number,
    pageSize: number
  ): Promise<BasicResponse<UserJobApplicationResponsePaginatedResponse>> => {
    try {
      const response = await (api as AxiosInstance).get(
        "/api/JobApplication/user/all",
        { params: { UserId: userId, PageNumber: pageNumber, PageSize: pageSize } }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
  getJobApplicants: async (
    postId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<BasicResponse<JobApplicantsResponsePaginatedResponse>> => {
    try {
      const response = await (api as AxiosInstance).get(
        "/api/JobApplication/applicants/all",
        { params: { PostId: postId, PageNumber: pageNumber, PageSize: pageSize } }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Existing mock functions (keeping for now, will remove if no longer needed)
  getJobById: async (jobId: string): Promise<Job> => {
    try {
      const job = mockJobs[jobId];
      if (!job) {
        throw new Error('Job not found');
      }
      return job;
    } catch (error: any) {
      console.error('Error fetching job:', error);
      throw error;
    }
  },

  // submitApplication: async (jobId: string, applicationData: ApplyFormData): Promise<ApplyResult> => {
  //   try {
  //     await new Promise(resolve => setTimeout(resolve, 1000));
      
  //     return {
  //       success: true,
  //       applicationId: `app_${Date.now()}`,
  //       message: 'Application submitted successfully'
  //     };
  //   } catch (error: any) {
  //     console.error('Error submitting application:', error);
  //     throw error;
  //   }
  // },
  getAllPosts: async (
    pageNumber: number,
    pageSize: number
  ): Promise<JobPostResponsePaginatedResponse> => {
    try {
      const response = await (api as AxiosInstance).get("/api/JobPost/allPosts", {
        params: { PageNumber: pageNumber, PageSize: pageSize },
      });
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  }
};

export default jobService;
